'use client';

import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

export function CronGenerator() {
  const [cronExpression, setCronExpression] = useState('* * * * *');
  const [minute, setMinute] = useState('*');
  const [hour, setHour] = useState('*');
  const [day, setDay] = useState('*');
  const [month, setMonth] = useState('*');
  const [weekday, setWeekday] = useState('*');
  const [command, setCommand] = useState('/path/to/script.sh');
  const [copied, setCopied] = useState(false);
  const [humanReadable, setHumanReadable] = useState('');

  const presets = [
    { name: 'Jede Minute', value: '* * * * *', desc: 'Läuft jede Minute' },
    { name: 'Stündlich', value: '0 * * * *', desc: 'Läuft jede Stunde zu Minute 0' },
    { name: 'Täglich (Mitternacht)', value: '0 0 * * *', desc: 'Läuft täglich um Mitternacht' },
    { name: 'Täglich (Mittag)', value: '0 12 * * *', desc: 'Läuft täglich um 12:00 Uhr' },
    { name: 'Wöchentlich (Montag)', value: '0 0 * * 1', desc: 'Läuft jeden Montag um Mitternacht' },
    { name: 'Monatlich', value: '0 0 1 * *', desc: 'Läuft am 1. jeden Monats um Mitternacht' },
    { name: 'Alle 5 Minuten', value: '*/5 * * * *', desc: 'Läuft alle 5 Minuten' },
    { name: 'Alle 15 Minuten', value: '*/15 * * * *', desc: 'Läuft alle 15 Minuten' },
    { name: 'Werktags um 9 Uhr', value: '0 9 * * 1-5', desc: 'Läuft Mo-Fr um 9:00 Uhr' },
  ];

  useEffect(() => {
    const expr = `${minute} ${hour} ${day} ${month} ${weekday}`;
    setCronExpression(expr);
    generateHumanReadable(expr);
  }, [minute, hour, day, month, weekday]);

  const generateHumanReadable = (expr) => {
    const [m, h, d, mo, w] = expr.split(' ');
    let readable = 'Läuft ';

    // Minute
    if (m === '*') readable += 'jede Minute';
    else if (m.startsWith('*/')) readable += `alle ${m.slice(2)} Minuten`;
    else readable += `bei Minute ${m}`;

    // Hour
    if (h !== '*') {
      if (h.startsWith('*/')) readable += `, alle ${h.slice(2)} Stunden`;
      else readable += `, um ${h} Uhr`;
    }

    // Day
    if (d !== '*') {
      if (d.startsWith('*/')) readable += `, alle ${d.slice(2)} Tage`;
      else readable += `, am ${d}. Tag des Monats`;
    }

    // Month
    if (mo !== '*') {
      const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
      readable += `, im ${months[parseInt(mo) - 1] || mo}`;
    }

    // Weekday
    if (w !== '*') {
      const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
      if (w.includes('-')) {
        const [start, end] = w.split('-');
        readable += `, von ${days[start]} bis ${days[end]}`;
      } else {
        readable += `, am ${days[parseInt(w)] || w}`;
      }
    }

    setHumanReadable(readable);
  };

  const loadPreset = (presetValue) => {
    const [m, h, d, mo, w] = presetValue.split(' ');
    setMinute(m);
    setHour(h);
    setDay(d);
    setMonth(mo);
    setWeekday(w);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const fullCronLine = `${cronExpression} ${command}`;

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        Cron Job Generator
      </h3>
      <p className="text-gray-700 mb-6">
        Erstelle Cron-Ausdrücke mit einem visuellen Editor. Perfekt für Linux/Unix Cron Jobs.
      </p>

      {/* Presets */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-3">
          Vorgefertigte Zeitpläne
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {presets.map((preset, index) => (
            <button
              key={index}
              onClick={() => loadPreset(preset.value)}
              className="text-left p-3 bg-gray-100 hover:bg-accent hover:text-white rounded-lg transition-colors"
            >
              <div className="font-semibold text-sm">{preset.name}</div>
              <div className="text-xs text-gray-600 hover:text-gray-200 mt-1">{preset.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Fields */}
      <div className="mb-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-semibold mb-2">
              Minute (0-59)
            </label>
            <input
              type="text"
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
              placeholder="* oder 0-59"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">* = jede, */5 = alle 5</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Stunde (0-23)
            </label>
            <input
              type="text"
              value={hour}
              onChange={(e) => setHour(e.target.value)}
              placeholder="* oder 0-23"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">* = jede</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Tag (1-31)
            </label>
            <input
              type="text"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              placeholder="* oder 1-31"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">* = jeder</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Monat (1-12)
            </label>
            <input
              type="text"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              placeholder="* oder 1-12"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">* = jeder</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Wochentag (0-6)
            </label>
            <input
              type="text"
              value={weekday}
              onChange={(e) => setWeekday(e.target.value)}
              placeholder="* oder 0-6"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">0 = So, 1-5 = Werktags</p>
          </div>
        </div>

        {/* Command */}
        <div>
          <label className="block text-sm font-semibold mb-2">
            Befehl / Script
          </label>
          <input
            type="text"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            placeholder="/pfad/zum/script.sh"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm"
          />
        </div>
      </div>

      {/* Output */}
      <div className="space-y-4">
        {/* Human Readable */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-blue-900 mb-1">Beschreibung:</p>
          <p className="text-blue-800">{humanReadable}</p>
        </div>

        {/* Cron Expression */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold">
              Cron Ausdruck
            </label>
            <button
              onClick={() => copyToClipboard(cronExpression)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              )}
            >
              {copied ? '✓ Kopiert!' : 'Kopieren'}
            </button>
          </div>
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg">
            <pre className="text-lg font-mono">
              {cronExpression}
            </pre>
          </div>
        </div>

        {/* Full Cron Line */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold">
              Komplette Crontab-Zeile
            </label>
            <button
              onClick={() => copyToClipboard(fullCronLine)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-accent hover:bg-accent-dark text-white'
              )}
            >
              {copied ? '✓ Kopiert!' : 'Kopieren'}
            </button>
          </div>
          <div className="bg-gray-900 text-green-400 p-4 rounded-lg">
            <pre className="text-sm font-mono break-all whitespace-pre-wrap">
              {fullCronLine}
            </pre>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800 mb-2">
            <strong>💡 Installation:</strong>
          </p>
          <ol className="text-sm text-yellow-800 space-y-1 list-decimal list-inside">
            <li>Öffne Terminal und tippe: <code className="bg-yellow-100 px-1 py-0.5 rounded">crontab -e</code></li>
            <li>Füge die kopierte Zeile ein</li>
            <li>Speichern und beenden (meist CTRL+X, dann Y, dann Enter)</li>
            <li>Prüfe mit: <code className="bg-yellow-100 px-1 py-0.5 rounded">crontab -l</code></li>
          </ol>
        </div>
      </div>

      {/* Feature List */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4">Cron Format Hilfe</h4>
        <div className="space-y-2 text-sm">
          <p><strong>Syntax:</strong> <code className="bg-gray-200 px-2 py-1 rounded font-mono">Minute Stunde Tag Monat Wochentag Befehl</code></p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
            <div><code className="text-accent">*</code> = Jeder Wert</div>
            <div><code className="text-accent">*/n</code> = Alle n Intervalle</div>
            <div><code className="text-accent">1,2,3</code> = Mehrere Werte</div>
            <div><code className="text-accent">1-5</code> = Bereich von Werten</div>
          </div>
        </div>
      </div>
    </div>
  );
}
